import { useMemo, useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, Search, Check } from 'lucide-react';
import { Button, cn } from '@jeraisonne/ui';
import { useThemesForPicker } from '@/lib/admin/pipeline-queries';
import { useLaunchPipeline } from '@/lib/admin/pipeline-mutations';
import {
  PIPELINE_STEPS,
  PIPELINE_MODELS,
  PIPELINE_MODEL_IDS,
  DEFAULT_STEP_MODELS,
  type PipelineLlmStep,
  type PipelineModelId,
  type StepRole,
} from '@/lib/admin/pipeline-models';

const inputCls =
  'border-ink-200 text-ink-900 placeholder:text-ink-400 focus:ring-primary-400 w-full rounded-xl border px-3 py-2.5 font-sans text-[14px] focus:outline-none focus:ring-2';
const labelCls = 'font-display text-ink-700 text-[12px] font-semibold';

export function TutorAdminPipelineNewPage() {
  const navigate = useNavigate();
  const { data: themes, isLoading: themesLoading } = useThemesForPicker();
  const launch = useLaunchPipeline();

  const [selectedThemeIds, setSelectedThemeIds] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [particularContext, setParticularContext] = useState('');
  const [parallelLimit, setParallelLimit] = useState(5);
  const [maxStatement, setMaxStatement] = useState(3);
  const [maxSolution, setMaxSolution] = useState(3);
  const [easy, setEasy] = useState(30);
  const [medium, setMedium] = useState(50);
  const [hard, setHard] = useState(20);
  const [stepModels, setStepModels] =
    useState<Record<PipelineLlmStep, PipelineModelId>>(DEFAULT_STEP_MODELS);

  function setStepModel(step: PipelineLlmStep, model: PipelineModelId) {
    setStepModels((prev) => ({ ...prev, [step]: model }));
  }
  /** Règle toutes les étapes d'un rôle sur le même modèle (raccourci). */
  function setRoleModels(role: StepRole, model: PipelineModelId) {
    setStepModels((prev) => {
      const next = { ...prev };
      for (const s of PIPELINE_STEPS) if (s.role === role) next[s.step] = model;
      return next;
    });
  }

  const distributionSum = easy + medium + hard;
  const distributionOk = distributionSum === 100;

  const filteredThemes = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (themes ?? []).filter((t) => !q || t.title.toLowerCase().includes(q));
  }, [themes, search]);

  function toggleTheme(id: string) {
    setSelectedThemeIds((prev) =>
      prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id],
    );
  }

  const canSubmit = selectedThemeIds.length > 0 && distributionOk && !launch.isPending;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    await launch.mutateAsync({
      themeIds: selectedThemeIds,
      particularContext: particularContext.trim() || null,
      parallelLimit,
      difficultyDistribution: { easy, medium, hard },
      maxIterationsStatement: maxStatement,
      maxIterationsSolution: maxSolution,
      stepModels,
    });
    navigate({ to: '/tutor/admin/pipeline' });
  }

  return (
    <div className="mx-auto max-w-xl p-4 sm:p-6">
      <Link
        to="/tutor/admin/pipeline"
        className="text-ink-500 hover:text-ink-900 mb-5 inline-flex items-center gap-1.5 font-sans text-[13px] transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        Pipeline
      </Link>

      <h1 className="font-display text-ink-900 mb-6 text-[22px] font-extrabold">
        Nouveau pipeline
      </h1>

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Thèmes */}
        <div className="flex flex-col gap-1.5">
          <label className={labelCls}>
            Thèmes{' '}
            <span className="text-ink-400 font-normal">
              ({selectedThemeIds.length} sélectionné{selectedThemeIds.length !== 1 ? 's' : ''})
            </span>
          </label>
          <div className="relative">
            <Search className="text-ink-400 absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher un thème…"
              className={cn(inputCls, 'pl-9')}
            />
          </div>
          <div className="border-ink-200 mt-1 max-h-60 overflow-y-auto rounded-xl border">
            {themesLoading ? (
              <p className="text-ink-400 p-3 font-sans text-[13px]">Chargement…</p>
            ) : filteredThemes.length === 0 ? (
              <p className="text-ink-400 p-3 font-sans text-[13px]">Aucun thème.</p>
            ) : (
              filteredThemes.map((t) => {
                const selected = selectedThemeIds.includes(t.id);
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => toggleTheme(t.id)}
                    className={cn(
                      'flex min-h-11 w-full items-center gap-2.5 px-3 py-2 text-left transition-colors',
                      selected ? 'bg-primary-50' : 'hover:bg-ink-50',
                    )}
                  >
                    <span
                      className={cn(
                        'flex h-5 w-5 shrink-0 items-center justify-center rounded-md border',
                        selected
                          ? 'bg-primary-600 border-primary-600 text-white'
                          : 'border-ink-300',
                      )}
                    >
                      {selected && <Check className="h-3.5 w-3.5" />}
                    </span>
                    <span className="font-sans text-[14px]">{t.title}</span>
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* Contexte particulier */}
        <div className="flex flex-col gap-1.5">
          <label className={labelCls}>
            Contexte particulier{' '}
            <span className="text-ink-400 font-normal">(optionnel, recommandé)</span>
          </label>
          <textarea
            value={particularContext}
            onChange={(e) => setParticularContext(e.target.value)}
            rows={3}
            maxLength={4000}
            placeholder="ex. voiture électrique vs thermique, élections municipales…"
            className={cn(inputCls, 'resize-none')}
          />
        </div>

        {/* Parallélisme */}
        <div className="flex flex-col gap-1.5">
          <label className={labelCls}>
            Workers en parallèle <span className="text-primary-600 font-bold">{parallelLimit}</span>
          </label>
          <input
            type="range"
            min={1}
            max={10}
            value={parallelLimit}
            onChange={(e) => setParallelLimit(Number(e.target.value))}
            className="accent-primary-600 h-2 w-full"
          />
        </div>

        {/* Itérations max */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <label className={labelCls}>Itérations max — énoncé</label>
            <input
              type="number"
              min={1}
              max={10}
              value={maxStatement}
              onChange={(e) => setMaxStatement(Number(e.target.value) || 1)}
              className={inputCls}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className={labelCls}>Itérations max — corrigé</label>
            <input
              type="number"
              min={1}
              max={10}
              value={maxSolution}
              onChange={(e) => setMaxSolution(Number(e.target.value) || 1)}
              className={inputCls}
            />
          </div>
        </div>

        {/* Distribution de difficulté */}
        <div className="flex flex-col gap-1.5">
          <label className={labelCls}>
            Distribution de difficulté{' '}
            <span
              className={cn('font-normal', distributionOk ? 'text-emerald-600' : 'text-accent-600')}
            >
              (somme : {distributionSum}%)
            </span>
          </label>
          <div className="grid grid-cols-3 gap-2">
            {(
              [
                ['Facile', easy, setEasy],
                ['Moyen', medium, setMedium],
                ['Difficile', hard, setHard],
              ] as [string, number, (v: number) => void][]
            ).map(([lab, val, set]) => (
              <div key={lab} className="flex flex-col gap-1">
                <span className="text-ink-500 font-sans text-[11px]">{lab}</span>
                <input
                  type="number"
                  min={0}
                  max={100}
                  value={val}
                  onChange={(e) => set(Math.max(0, Math.min(100, Number(e.target.value) || 0)))}
                  className={inputCls}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Modèles IA par étape */}
        <div className="flex flex-col gap-2">
          <label className={labelCls}>
            Modèles IA par étape{' '}
            <span className="text-ink-400 font-normal">(défauts économiques pré-réglés)</span>
          </label>
          {(['generation', 'verification'] as StepRole[]).map((role) => {
            // Les étapes `v2_*` (génération « blocs natifs ») ne sont pas encore
            // utilisées par le pipeline thème — masquées de cet écran jusque-là.
            const steps = PIPELINE_STEPS.filter(
              (s) => s.role === role && !s.step.startsWith('v2_'),
            );
            return (
              <div key={role} className="border-ink-200 rounded-xl border p-3">
                <div className="mb-2 flex items-center justify-between gap-2">
                  <span className="font-display text-ink-700 text-[12px] font-bold">
                    {role === 'generation' ? 'Génération' : 'Vérification'}
                  </span>
                  <select
                    value=""
                    onChange={(e) => {
                      if (e.target.value) setRoleModels(role, e.target.value as PipelineModelId);
                    }}
                    aria-label={`Régler toutes les étapes de ${role === 'generation' ? 'génération' : 'vérification'}`}
                    className="border-ink-200 text-ink-600 min-h-11 rounded-lg border bg-white px-2 font-sans text-[12px]"
                  >
                    <option value="">Tout régler…</option>
                    {PIPELINE_MODEL_IDS.map((id) => (
                      <option key={id} value={id}>
                        {PIPELINE_MODELS[id].label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {steps.map((s) => (
                    <div key={s.step} className="flex flex-col gap-1">
                      <span className="text-ink-500 font-sans text-[11px]">{s.label}</span>
                      <select
                        value={stepModels[s.step]}
                        onChange={(e) => setStepModel(s.step, e.target.value as PipelineModelId)}
                        className={cn(inputCls, 'min-h-11')}
                      >
                        {PIPELINE_MODEL_IDS.map((id) => (
                          <option key={id} value={id}>
                            {PIPELINE_MODELS[id].label}
                          </option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {launch.isError && (
          <p className="text-accent-600 font-sans text-[13px]">{launch.error?.message}</p>
        )}

        {/* Actions — primaire en haut du DOM, visuellement en bas */}
        <div className="flex flex-col-reverse gap-3 pt-2 sm:flex-row">
          <Link to="/tutor/admin/pipeline" className="sm:flex-1">
            <Button type="button" variant="outline" className="w-full">
              Annuler
            </Button>
          </Link>
          <Button type="submit" className="w-full sm:flex-1" disabled={!canSubmit}>
            {launch.isPending ? 'Lancement…' : 'Lancer le pipeline'}
          </Button>
        </div>
        {!distributionOk && (
          <p className="text-ink-400 font-sans text-[11px]">
            La distribution de difficulté doit sommer à 100 %.
          </p>
        )}
      </form>
    </div>
  );
}
